import { memo, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import MoonLoader from "react-spinners/MoonLoader";
import MessageBubble from "./MessageBubble";
import DateSeparator from "./DateSeparator";
import { groupMessagesByDate, shouldShowTail } from "../../utils/messageProcessor";
import "./ChatMessages.css";

/**
 * Lista de mensagens do chat
 */
const ChatMessages = memo(({
  messages,
  isLoading,
  hasMore,
  onLoadMore,
  isLoadingMore,
  currentUserId,
  onReplyComment,
}) => {
  const messagesEndRef = useRef(null);
  const prevMessagesLength = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const initialScrollDone = useRef(false);
  // Cache da posição do scroll ANTES de cada novo render, capturado
  // via onScroll. Permite saber se o usuário estava no fim antes da
  // nova mensagem entrar (sem ser confundido pela altura recém-adicionada).
  const wasNearBottomRef = useRef(true);
  // Distância em pixels do fim que ainda conta como "está no fim".
  // Acima disso, o usuário rolou pra cima pra ler — auto-scroll é
  // suprimido pra não interromper a leitura.
  const NEAR_BOTTOM_THRESHOLD = 80;

  // Agrupar mensagens por data
  const groupedMessages = useMemo(() => {
    return groupMessagesByDate(messages);
  }, [messages]);

  // Localiza o container do PerfectScrollbar a partir do anchor.
  const getScrollContainer = () => {
    const anchor = messagesEndRef.current;
    if (!anchor) return null;
    return anchor.closest(".scrollbar-container");
  };

  // Handler de scroll pra atualizar a flag near-bottom. Esse handler
  // roda quando o usuário rola — captura o estado ANTES da próxima
  // mensagem entrar, evitando que a altura da nova msg falsifique a leitura.
  const handleScroll = () => {
    const c = getScrollContainer();
    if (!c) return;
    wasNearBottomRef.current =
      c.scrollHeight - c.scrollTop - c.clientHeight < NEAR_BOTTOM_THRESHOLD;
  };

  const scrollToBottom = (smooth = false) => {
    const c = getScrollContainer();
    if (!c) return;
    if (smooth) {
      c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
    } else {
      c.scrollTop = c.scrollHeight;
    }
  };

  // Rastreia quando o usuário clicou em "Carregar mais"
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Auto-scroll apenas se o usuário está perto do fim. Se ele rolou
  // pra cima pra ler histórico antigo, não força scroll — preserva a
  // posição. Comportamento padrão de WhatsApp/Telegram.
  useLayoutEffect(() => {
    const grew =
      messages.length > prevMessagesLength.current &&
      prevMessagesLength.current > 0;
    prevMessagesLength.current = messages.length;
    if (!grew) return;
    if (isLoadingMoreRef.current) return;
    // Decide com base na posição ANTES da nova mensagem (capturada
    // pelo onScroll). Se o usuário rolou pra cima pra ler histórico,
    // wasNearBottomRef.current é false → não scrolla.
    if (!wasNearBottomRef.current) return;
    setTimeout(() => scrollToBottom(false), 0);
    setTimeout(() => scrollToBottom(false), 100);
  }, [messages.length]);

  // Reseta o controle de scroll inicial quando um novo chat começa a carregar
  useEffect(() => {
    if (isLoading) {
      initialScrollDone.current = false;
    }
  }, [isLoading]);

  // Scroll para baixo apenas no carregamento inicial (sem animação)
  useEffect(() => {
    if (!isLoading && messages.length > 0 && !initialScrollDone.current) {
      initialScrollDone.current = true;
      setTimeout(() => scrollToBottom(false), 50);
    }
  }, [isLoading, messages.length]);

  // Verificar se mensagem é do usuário atual
  const isFromCurrentUser = (message) => {
    if (typeof message.isMe === "boolean") {
      return message.isMe;
    }
    return message.fromMe === true ||
           message.from_me === true ||
           message.sender === "Me" ||
           message.sender_id === currentUserId;
  };

  // Renderiza conteúdo interno baseado no estado
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="sc-messages__state-container">
          <MoonLoader size={40} color="#3b82f6" />
          <span>Carregando mensagens...</span>
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="sc-messages__state-container">
          <span className="sc-messages__empty-icon">💬</span>
          <span className="sc-messages__empty-text">Nenhuma mensagem ainda</span>
          <span className="sc-messages__empty-hint">
            As mensagens aparecerão aqui quando a conversa começar
          </span>
        </div>
      );
    }

    return (
      <PerfectScrollbar
        options={{ suppressScrollX: true }}
        onScrollY={handleScroll}
      >
        <div className="sc-messages__container">
          {/* Loader de mais mensagens */}
          {isLoadingMore && (
            <div className="sc-messages__loading-more">
              <MoonLoader size={20} color="#3b82f6" />
            </div>
          )}

          {/* Indicador de mais mensagens disponíveis */}
          {hasMore && !isLoadingMore && (
            <button
              className="sc-messages__load-more"
              onClick={onLoadMore}
            >
              Carregar mensagens anteriores
            </button>
          )}

          {/* Mensagens agrupadas por data */}
          {Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date} className="sc-messages__date-group">
              <DateSeparator date={date} />

              {dateMessages.map((message, index) => (
                <MessageBubble
                  key={message.id || `${message.timestamp}-${index}`}
                  message={message}
                  isFromMe={isFromCurrentUser(message)}
                  showTail={shouldShowTail(dateMessages, index, isFromCurrentUser)}
                  onReplyComment={onReplyComment}
                />
              ))}
            </div>
          ))}

          {/* Âncora para auto-scroll — usada para encontrar o container */}
          <div ref={messagesEndRef} />
        </div>
      </PerfectScrollbar>
    );
  };

  return (
    <div className="sc-messages">
      {renderContent()}
    </div>
  );
});

ChatMessages.displayName = "ChatMessages";

export default ChatMessages;
